import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import popup_bg1 from '../../Common/Images/ImagesForAlert/popup_bg1.png';
import alert_close_n from '../../Common/Images/ImagesForAlert/alert_close_n.png';
import alert_close_o from '../../Common/Images/ImagesForAlert/alert_close_o.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

export interface Competition {
    CompID: number;
    Title: string;
    Type: string;
    Price: number;
    NoOfWinners: number;
    CluePrice: number;
    Answer: string;
    StartDate: string;
    EntryClosesFormatted: string;
    AllocatedTo: string;
    Status: string;
    TotalEntries: number;
    UniqueEntries: number;
    WinnerFlag: string;
    WinnerStatus: string;
}

interface Props {
    comp: Competition; 
    onClose: () => void;
}

const EL_CompViewEntrants: React.FC<Props> = ({ comp, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [entrants, setEntrants] = useState<any[]>([]);
    const [alertMsg, setAlertMsg] = useState('');

    useEffect(() => {
        if (comp?.CompID) {
            fetchEntrants();
        }
    }, [comp]);

    const fetchEntrants = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/manage-comps/${comp.CompID}/entrants/?type=${comp.Type}`);
            if (res.ok) {
                const data = await res.json();
                setEntrants(data);
            } else {
                setAlertMsg("Failed to load entrants");
            }
        } catch (e) {
            console.error(e);
            setAlertMsg("Network Error");
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        overlay: {
            position: 'fixed' as const, 
            top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', justifyContent: 'center', alignItems: 'center'
        },
        modal: {
            backgroundColor: 'azure', 
            width: '100%', height: '100%', 
            display: 'flex', flexDirection: 'column' as const,
            border: 'none', overflow: 'hidden',
            fontFamily: 'Arial, sans-serif', fontSize: '9pt'
        },
        popupHeader: {
            backgroundImage: `url(${popup_bg1})`,
            backgroundRepeat: 'repeat-x',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '1px solid #000', height: '30px',
            color: 'white', fontFamily: 'Calibri, Arial, sans-serif',
            fontSize: '11pt', fontWeight: 'bold' as const, paddingLeft: '10px'
        },
        maroonHeader: {
            backgroundColor: '#800000', color: 'White', padding: '5px',
            fontSize: '12pt', fontWeight: 'bold' as const, textAlign: 'center' as const,
            marginBottom: '5px'
        },
        infoTable: {
            width: '100%', padding: '0 10px 5px 10px', fontSize: '12px', fontFamily: 'Arial'
        },
        infoRow: { height: '20px' },
        gridContainer: {
            flex: 1, 
            overflow: 'auto', 
            border: '1px solid #a3c0e8', 
            margin: '0 10px 5px 10px'
        },
        gridHeader: {
            backgroundColor: '#C7DDFD', fontWeight: 'bold' as const, 
            position: 'sticky' as const, top: 0, 
            borderBottom: '1px solid #a3c0e8',
            fontSize: '12px',
            color: '#000000'
        },
        th: {
            color: '#2859ac',
            padding: '8px',
            fontSize: '13px',
            borderRight: '1px solid #a3c0e8', 
            textAlign: 'left' as const,
            whiteSpace: 'nowrap' as const,
            fontWeight: 'normal' as const
        },
        td: { 
            padding: '8px', 
            borderRight: '1px solid #a3c0e8', 
            // borderBottom: '1px solid #a3c0e8',
            fontSize: '12px',
            color: '#000000',
            borderLeft: '1px solid a3c0e8',
        },
        footer: {
            padding: '10px', textAlign: 'left' as const, 
            borderTop: '1px solid #a3c0e8', backgroundColor: '#F0F0F0'
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                
                <div style={styles.popupHeader}>
                    <span>&nbsp;</span> 
                    <img 
                        src={alert_close_n}
                        alt="Close"
                        onClick={onClose}
                        style={{ cursor: 'pointer' }}
                        onMouseOver={(e) => e.currentTarget.src = alert_close_o}
                        onMouseOut={(e) => e.currentTarget.src = alert_close_n}
                    />
                </div>

                <div style={styles.maroonHeader}>
                    Competition Entries
                </div>

                <div style={{ padding: '5px' }}>
                    <table style={styles.infoTable}>
                        <tbody>
                            <tr style={styles.infoRow}>
                                <td width="30%"><b>&nbsp;&nbsp;&nbsp;Competition Title:</b> {comp.Title}</td>
                                <td width="30%"><b>Total Entries:</b> {comp.TotalEntries}</td>
                                <td width="40%"><b>Competition Type:</b> {comp.Type}</td>
                            </tr>
                            <tr style={styles.infoRow}>
                                <td><b>&nbsp;&nbsp;&nbsp;Entry Close:</b> {comp.EntryClosesFormatted}</td>
                                <td><b>Unique Entries:</b> {comp.UniqueEntries}</td>
                                <td><b>Number of Winners:</b> {comp.NoOfWinners}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={styles.gridContainer}>
                    {loading ? <Loading /> : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={styles.gridHeader}>
                                <tr>
                                    <th style={{...styles.th, width: '30px'}}>#</th>
                                    <th style={styles.th}>SurName</th>
                                    <th style={styles.th}>First Name</th>
                                    <th style={styles.th}>Grade Year</th>
                                    <th style={styles.th}>SchoolCode</th>
                                    <th style={styles.th}>FamilyCode</th>
                                    <th style={styles.th}>Login</th>
                                    <th style={styles.th}>Entries</th>
                                    <th style={styles.th}>Date Entered</th>
                                    {comp.Type === 'Grand' && <th style={styles.th}>Clues Purchased</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {entrants.map((row, idx) => (
                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f4faff' : 'white' }}>
                                        <td style={styles.td}>{idx + 1}</td>
                                        <td style={styles.td}>{row.SurName}</td>
                                        <td style={styles.td}>{row.FirstName}</td>
                                        <td style={styles.td}>{row.GradeYear}</td>
                                        <td style={styles.td}>{row.SchoolCode}</td>
                                        <td style={styles.td}>{row.FamilyCode}</td>
                                        <td style={styles.td}>{row.Login}</td>
                                        <td style={styles.td}>{row.Entries}</td>
                                        <td style={styles.td}>{row.DateEntered}</td>
                                        {comp.Type === 'Grand' && <td style={styles.td}>{row.CluesPurchased}</td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={{...styles.footer, backgroundColor: 'azure'}}>
                    <button onClick={onClose} style={{ width: '120px', height: '25px', cursor: 'pointer' }}>Close</button>
                </div>
            </div>
            {alertMsg && (
                <CustomAlert 
                    message={alertMsg} 
                    onClose={() => setAlertMsg('')} 
                />
            )}
        </div>
    );
};

export default EL_CompViewEntrants;